import { Component } from 'react';
import {
  GremlinComponentResult,
  GremlinMapResult,
  GremlinPathResult,
  GremlinReferenceResult,
  GremlinResult,
  GremlinResultType,
} from '../types';
import { getResultType } from '../utils';
import { GremlinComponentsResultTable as GremlinResultsTableComponent } from './GremlinResultsTableComponent';
import { GremlinResultsTablePath } from './GremlinResultsTablePath';
import { GremlinResultsTableReference } from './GremlinResultsTableReference';
import { GremlinResultsTableMap } from './GremlinResultsTableMap';
import { GremlinResultsTableDirect } from './GremlinResultsTableDirect';
import { SortByFn, SortById } from '@ardoq/pagination';
import { logError } from '@ardoq/logging';
import { SortOrder } from '@ardoq/api-types';
import type { FieldLabelByName } from 'models/utils/fieldOps';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import type { TableEmptyStateLabels } from '@ardoq/table';

interface GremlinResultsTableProps {
  results: GremlinResult[];
  resultKeys?: string[];
  selectedColumns: string[];
  showRaw?: boolean;
  printable?: boolean;
  sortBy: SortByFn;
  sortOrder?: SortOrder;
  sortById: SortById;
  fieldLabelByName: FieldLabelByName;
  emptyStateLabels: TableEmptyStateLabels;
  locale: Locale;
  i18nLocale: I18nLocale;
}

const GremlinResultsTableSwitch = (props: GremlinResultsTableProps) => {
  const printable = props.printable || false;
  const resultType =
    (props.showRaw && GremlinResultType.DIRECT) ||
    getResultType(props.results[0]);
  return (
    <>
      {resultType === GremlinResultType.COMPONENT && (
        <GremlinResultsTableComponent
          results={props.results as GremlinComponentResult[]}
          selectedColumns={props.selectedColumns}
          printable={printable}
          sortBy={props.sortBy}
          sortOrder={props.sortOrder}
          sortById={props.sortById}
          fieldLabelByName={props.fieldLabelByName}
          locale={props.locale}
          i18nLocale={props.i18nLocale}
        />
      )}
      {resultType === GremlinResultType.PATH && (
        <GremlinResultsTablePath
          results={props.results as GremlinPathResult[]}
          printable={printable}
          sortBy={props.sortBy}
          sortOrder={props.sortOrder}
          sortById={props.sortById}
          i18nLocale={props.i18nLocale}
        />
      )}
      {resultType === GremlinResultType.REFERENCE && (
        <GremlinResultsTableReference
          results={props.results as GremlinReferenceResult[]}
          selectedColumns={props.selectedColumns}
          printable={printable}
          sortBy={props.sortBy}
          sortOrder={props.sortOrder}
          sortById={props.sortById}
          fieldLabelByName={props.fieldLabelByName}
          locale={props.locale}
          i18nLocale={props.i18nLocale}
        />
      )}
      {resultType === GremlinResultType.MAP && (
        <GremlinResultsTableMap
          results={props.results as GremlinMapResult[]}
          columnKeys={props.resultKeys}
          printable={printable}
          sortBy={props.sortBy}
          sortOrder={props.sortOrder}
          sortById={props.sortById}
          emptyStateLabels={props.emptyStateLabels}
          locale={props.locale}
          i18nLocale={props.i18nLocale}
        />
      )}
      {resultType === GremlinResultType.DIRECT && (
        <GremlinResultsTableDirect
          results={props.results}
          emptyStateLabels={props.emptyStateLabels}
          locale={props.locale}
          i18nLocale={props.i18nLocale}
        />
      )}
    </>
  );
};

type GremlinResultsTableState = {
  forceRawResults: boolean;
};

class GremlinResultsTable extends Component<
  GremlinResultsTableProps,
  GremlinResultsTableState
> {
  constructor(props: GremlinResultsTableProps) {
    super(props);
    this.state = {
      forceRawResults: false,
    };
  }
  componentDidCatch(error: Error) {
    logError(error, 'Error while parsing gremlin results');
    this.setState({
      forceRawResults: true,
    });
  }
  componentDidUpdate(prevProps: GremlinResultsTableProps) {
    if (
      this.state.forceRawResults &&
      prevProps.results !== this.props.results
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        forceRawResults: false,
      });
    }
  }
  render() {
    const showRaw = this.props.showRaw || this.state.forceRawResults;
    return (
      <>
        {this.state.forceRawResults && (
          <p>
            <em>
              {t(
                this.props.i18nLocale,
                'search:gremlinResultsTable.forceRawResultsWarning',
                'We were unable to format and render your results nicely - your query results are being shown as raw JSON'
              )}
            </em>
          </p>
        )}
        <GremlinResultsTableSwitch {...this.props} showRaw={showRaw} />
      </>
    );
  }
}

export { GremlinResultsTable };
